import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CREDIT_NOTE_REASONS, formatMoney, moneyFromCents, REFUND_REASONS } from '@wodi/domain';
import type { BillView, ChargeView, RefundView } from '@wodi/shared';
import {
  Button,
  Dialog,
  DialogActions,
  MoneyField,
  Notice,
  RadioGroup,
  Select,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { billingKeys, payRefund, requestCreditNote, requestRefund } from '../api/billing.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import styles from './billing.module.css';
import { cents, money } from './format.ts';

export const CREDIT_REASON_LABELS: Record<(typeof CREDIT_NOTE_REASONS)[number], string> = {
  service_not_provided: 'Service not provided',
  charged_in_error: 'Charged in error',
  wrong_quantity: 'Wrong quantity',
  other: 'Other',
};

export const REFUND_REASON_LABELS: Record<(typeof REFUND_REASONS)[number], string> = {
  service_not_provided: 'Service not provided',
  overpayment: 'Overpayment',
  charged_in_error: 'Charged in error',
  other: 'Other',
};

export const REFUND_METHOD_LABELS: Record<RefundView['method'], string> = {
  cash: 'Cash from the drawer',
  mpesa_reversal: 'M-Pesa reversal',
  bank_transfer: 'Bank transfer',
};

/** Shared form for amount, reason and note, with the rules the API applies. */
function useReasonedAmount<R extends string>(max: bigint) {
  const [amount, setAmount] = useState<bigint | null>(max > 0n ? max : null);
  const [reason, setReason] = useState<R | null>(null);
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const amountOk = amount !== null && amount > 0n && amount <= max;
  const valid = amountOk && reason !== null && (reason !== 'other' || note.trim() !== '');
  return { amount, setAmount, reason, setReason, note, setNote, tried, setTried, amountOk, valid };
}

/**
 * Ask for a credit note on one billed charge (plan §2.9). Someone else with approval rights
 * decides; only an approved credit note changes what is owed.
 */
export function CreditNoteDialog({ charge, onClose }: { charge: ChargeView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const max = cents(charge.creditable);
  const form = useReasonedAmount<(typeof CREDIT_NOTE_REASONS)[number]>(max);
  const submit = useMutation({
    mutationFn: () => {
      return requestCreditNote(charge.invoiceId ?? '', {
        lines: [
          {
            invoiceLineId: charge.invoiceLineId ?? '',
            amountCents: (form.amount ?? 0n).toString(),
          },
        ],
        reasonCode: form.reason ?? 'other',
        ...(form.note.trim() === '' ? {} : { reasonNote: form.note.trim() }),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: billingKeys.all });
      toasts.show({ title: 'Credit note requested', description: 'Waiting for approval' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Ask for a credit note"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        <p>
          {`${charge.itemName} is on a bill. Up to ${formatMoney(moneyFromCents(max))} can be credited. Someone else with approval rights must approve it.`}
        </p>
        <MoneyField
          label="Amount to credit"
          value={form.amount}
          onChange={(v) => {
            form.setAmount(v);
          }}
          {...(form.tried && !form.amountOk
            ? { errorMessage: `Enter an amount up to ${formatMoney(moneyFromCents(max))}.` }
            : {})}
        />
        <Select
          label="Reason"
          placeholder="Choose a reason"
          options={CREDIT_NOTE_REASONS.map((r) => ({ id: r, label: CREDIT_REASON_LABELS[r] }))}
          value={form.reason}
          onChange={(v) => {
            form.setReason(v as (typeof CREDIT_NOTE_REASONS)[number]);
          }}
          {...(form.tried && form.reason === null ? { errorMessage: 'Choose a reason.' } : {})}
        />
        <TextArea
          label="Note"
          description={form.reason === 'other' ? 'Required when the reason is other.' : 'Optional.'}
          value={form.note}
          onChange={form.setNote}
          {...(form.tried && form.reason === 'other' && form.note.trim() === ''
            ? { errorMessage: 'Say why.' }
            : {})}
        />
        {submit.isError ? (
          <Notice tone="error">{errorText(submit.error, "The request wasn't sent.")}</Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isPending={submit.isPending}
          pendingLabel="Asking"
          onPress={() => {
            form.setTried(true);
            if (form.valid) submit.mutate();
          }}
        >
          Ask for approval
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Ask for a refund from one payment, up to what the patient has in credit (plan §2.8). */
export function RefundDialog({
  payment,
  onClose,
}: {
  payment: BillView['payments'][number];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const max = cents(payment.refundable);
  const form = useReasonedAmount<(typeof REFUND_REASONS)[number]>(max);
  const [method, setMethod] = useState<RefundView['method']>(
    payment.method === 'mpesa'
      ? 'mpesa_reversal'
      : payment.method === 'cash'
        ? 'cash'
        : 'bank_transfer',
  );
  const submit = useMutation({
    mutationFn: () =>
      requestRefund(payment.id, {
        amountCents: (form.amount ?? 0n).toString(),
        method,
        reasonCode: form.reason ?? 'other',
        ...(form.note.trim() === '' ? {} : { reasonNote: form.note.trim() }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: billingKeys.all });
      toasts.show({ title: 'Refund requested', description: 'Waiting for approval' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Ask for a refund"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        <p>
          {`Up to ${formatMoney(moneyFromCents(max))} can be refunded from this payment of ${money(payment.amount)}. Someone else with approval rights must approve it.`}
        </p>
        <MoneyField
          label="Amount to refund"
          value={form.amount}
          onChange={(v) => {
            form.setAmount(v);
          }}
          {...(form.tried && !form.amountOk
            ? { errorMessage: `Enter an amount up to ${formatMoney(moneyFromCents(max))}.` }
            : {})}
        />
        <RadioGroup
          label="Refund by"
          options={(Object.keys(REFUND_METHOD_LABELS) as RefundView['method'][]).map((m) => ({
            id: m,
            label: REFUND_METHOD_LABELS[m],
          }))}
          value={method}
          onChange={(v) => {
            setMethod(v as RefundView['method']);
          }}
        />
        <Select
          label="Reason"
          placeholder="Choose a reason"
          options={REFUND_REASONS.map((r) => ({ id: r, label: REFUND_REASON_LABELS[r] }))}
          value={form.reason}
          onChange={(v) => {
            form.setReason(v as (typeof REFUND_REASONS)[number]);
          }}
          {...(form.tried && form.reason === null ? { errorMessage: 'Choose a reason.' } : {})}
        />
        <TextArea
          label="Note"
          description={form.reason === 'other' ? 'Required when the reason is other.' : 'Optional.'}
          value={form.note}
          onChange={form.setNote}
          {...(form.tried && form.reason === 'other' && form.note.trim() === ''
            ? { errorMessage: 'Say why.' }
            : {})}
        />
        {submit.isError ? (
          <Notice tone="error">{errorText(submit.error, "The request wasn't sent.")}</Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isPending={submit.isPending}
          pendingLabel="Asking"
          onPress={() => {
            form.setTried(true);
            if (form.valid) submit.mutate();
          }}
        >
          Ask for approval
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Pay out an approved refund. Cash leaves this cashier's drawer; an M-Pesa or bank reversal
 * is made in the M-Pesa portal or the bank and recorded here by its reference.
 */
export function PayRefundDialog({ refund, onClose }: { refund: RefundView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [reference, setReference] = useState('');
  const [tried, setTried] = useState(false);
  const needsReference = refund.method !== 'cash';
  const valid = !needsReference || reference.trim().length >= 3;
  const submit = useMutation({
    mutationFn: () => payRefund(refund.id, needsReference ? reference.trim() : undefined),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: billingKeys.all });
      toasts.show({ title: 'Refund paid', description: refund.number });
      onClose();
    },
  });
  return (
    <Dialog
      title="Pay out refund"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        <p>
          {needsReference
            ? `Make the ${REFUND_METHOD_LABELS[refund.method].toLowerCase()} of ${money(refund.amount)} first, then enter its reference. Wodi doesn't move money itself.`
            : `Give ${money(refund.amount)} in cash from your drawer. It comes off your shift's expected cash.`}
        </p>
        {needsReference ? (
          <TextField
            label="Reversal reference"
            value={reference}
            onChange={setReference}
            {...(tried && !valid ? { errorMessage: 'Enter the reversal reference.' } : {})}
          />
        ) : null}
        {submit.isError ? (
          <Notice tone="error">{errorText(submit.error, "The refund wasn't recorded.")}</Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isPending={submit.isPending}
          pendingLabel="Recording"
          onPress={() => {
            setTried(true);
            if (valid) submit.mutate();
          }}
        >
          Pay out refund
        </Button>
      </DialogActions>
    </Dialog>
  );
}
